import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import {
	ArrowTrendingUpIcon,
	BanknotesIcon,
	BuildingOfficeIcon,
	CheckBadgeIcon,
	ClockIcon,
	CreditCardIcon,
	TicketIcon,
	ViewColumnsIcon,
	XMarkIcon,
} from "@heroicons/react/20/solid";
import { Actions as ALLOWANCEACTIONS } from "../../../../store/actions/allowanceActions";
import { Actions as AUTHENTICATION_ACTIONS } from "../../../../store/actions/authenticationActions";
import { Actions as COMPANY_ACTIONS } from "../../../../store/actions/companyActions";
import { Actions as OVERTIME_ACTIONS } from "../../../../store/actions/overtimeActions";
import { Actions as PAY_SCHEDULE_ACTIONS } from "../../../../store/actions/payScheduleActions";
import { ImagePlusIcon } from "../../icons/Solid";
import Allowances from "./Allowances";
import CompanyDetails from "./CompanyDetails";
import Overtimes from "./Overtimes";
import Billing from "./Billing";
import PaySchedules from "./PaySchedules";
import TimeAndAttendance from "./TimeAndAttendance";
import UploadLogo from "./UploadLogo";
import Confirmation from "./Confirmation";
import AdvisorCreateCompany from "./AdvisorCreateCompany";
import Modal from "@/common/Modal";

const companyPages = [
	{
		title: "Advisor",
		child: AdvisorCreateCompany,
		icon: ViewColumnsIcon,
		jurisdiction: ["UK", "MT"],
		isAdvisor: true,
	},
	{
		title: "Company Details",
		child: CompanyDetails,
		icon: BuildingOfficeIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Pay Schedules",
		child: PaySchedules,
		icon: TicketIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Overtime & Special Rates",
		child: Overtimes,
		icon: ArrowTrendingUpIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Allowances",
		child: Allowances,
		icon: BanknotesIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Billing",
		child: Billing,
		icon: CreditCardIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Time & Attendance",
		child: TimeAndAttendance,
		icon: ClockIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Logo Upload",
		child: UploadLogo,
		icon: ImagePlusIcon,
		jurisdiction: ["UK", "MT"],
	},
	{
		title: "Confirmation",
		child: Confirmation,
		icon: CheckBadgeIcon,
		jurisdiction: ["UK", "MT"],
	},
];

const NewCompanyModal = ({
	isNewCompanyModalOpen,
	setNewCompanyModal,
	createCompany,
	updateCompanyDetails,
	addPaySchedule,
	addOvertime,
	addAllowance,
	updateSetting,
	handleLogoUpload,
	switchCompany,
	newCompanyId,
	plan,
	advisor,
	isAdvisor,
	companyName,
	newOrg,
	loadedCompanySettings,
}) => {
	const [page, setPage] = useState(0);
	const [maxPage, setMaxPage] = useState(0);
	const lastPage = companyPages.length;

	useEffect(() => {
		if (page > maxPage) setMaxPage(page);
	}, [page]);

	const nextPage = () => {
		if (page + 1 === lastPage) {
			setNewCompanyModal();
			setPage(0);
		} else setPage(page + 1);
	};

	const prevPage = () => {
		setPage(page - 1);
	};

	const closeModal = () => {
		setPage(0);
		setNewCompanyModal();
		if (newOrg) switchCompany(newOrg);
	};

	const options = {
		advisorPage: isAdvisor,
		createCompany: (body) => createCompany(body, nextPage),
		updateCompanyDetails: (body) => updateCompanyDetails(body, nextPage),
		addPaySchedule,
		addOvertime,
		addAllowance,
		updateSetting,
		handleLogoUpload,
		switchCompany,
		closeModal,
		prevPage,
		nextPage,
		newCompanyId,
		loadedCompanySettings,
	};
	const planHasTimeAttendance = typeof plan == "undefined" || plan == "f73c2c10-aa39-4408-96e3-a68915bb9d3b";
	const filteredCompanyPages = companyPages
		?.filter((item) => (planHasTimeAttendance ? item.title != "Time & Attendance" : true))
		?.filter((item) => typeof item.isAdvisor === "undefined" || item.isAdvisor === isAdvisor);
	const currentPage = filteredCompanyPages[page];

	useEffect(() => {
		const billingPage = filteredCompanyPages?.findIndex((item) => item.title == "Billing");
		if (maxPage > billingPage) {
			setMaxPage(billingPage);
			return;
		}
		if (!companyName) setMaxPage(0);
	}, [plan, advisor, isNewCompanyModalOpen]);

	return (
		<Modal
			status={isNewCompanyModalOpen}
			close={closeModal}
			title={`${companyName ? companyName : "Add New Company"} - ${currentPage.title}`}
			width="75%"
		>
			<div
				className="block-content bg-white shadow-sm pb-4"
				style={{ paddingTop: 20, position: "sticky", top: "6rem", zIndex: 50 }}
			>
				<ul className="progress-bar-nav">
					{filteredCompanyPages.map((pageItem, index) => {
						return (
							<li
								style={{ width: `${(1 / filteredCompanyPages.length) * 100}%` }}
								className={`${page == index ? "current " : ""}${maxPage >= index ? "active" : ""}`}
							>
								<div className="progress-bar-nav-line"></div>
								<div
									className="progress-bar-nav-icon"
									style={{ cursor: maxPage >= index ? "pointer" : "auto" }}
									onClick={() => {
										if (maxPage < index) return;
										setPage(index);
									}}
								>
									{pageItem.icon && <pageItem.icon />}
								</div>
								{pageItem.title}
							</li>
						);
					})}
				</ul>
				<div className="clearfix" />
			</div>
			<div
				className="block-content max-h-[calc(95vh-120px-60px)] overflow-y-auto"
				style={{
					paddingBottom: "2rem",
					paddingLeft: "2rem",
					paddingRight: "2rem",
					paddingTop: 0,
				}}
			>
				<currentPage.child {...options} />
			</div>
			<div className="clearfix" />
		</Modal>
	);
};

const mapStateToProps = (state) => {
	return {
		isNewCompanyModalOpen: state.company.isNewCompanyModalOpen,
		newCompanyId: state.company?.newCompany?.org,
		plan: state.company?.newCompany?.plan,
		companyName: state.company?.newCompany?.name,
		isAdvisor: state.my?.profile?.isAdvisor,
		advisor: state.company?.newCompany?.advisor,
		newOrg: state.company.newCompany?.org,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setNewCompanyModal: () => dispatch(COMPANY_ACTIONS.setNewCompanyModal(false)),
	createCompany: (body, nextPage) => dispatch(COMPANY_ACTIONS.createCompany(body, nextPage)),
	updateCompanyDetails: (body, nextPage) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body, true, nextPage)),
	loadedCompanySettings: (body) => dispatch(COMPANY_ACTIONS.loadedCompanySettings(body, true)),
	addPaySchedule: () => dispatch(PAY_SCHEDULE_ACTIONS.addPaySchedule({ parent: null, newCompany: true })),
	addAllowance: (id) => dispatch(ALLOWANCEACTIONS.addAllowance(null, true, id)),
	addOvertime: (id) => dispatch(OVERTIME_ACTIONS.addOvertime(true, id)),
	handleLogoUpload: (file, org) => dispatch(COMPANY_ACTIONS.handleLogoUpload({ file, org })),
	updateSetting: (field, newValue) => dispatch(COMPANY_ACTIONS.updateSetting(field, newValue, true)),
	switchCompany: (companyId) => dispatch(AUTHENTICATION_ACTIONS.switchCompany(companyId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewCompanyModal);
